import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, RouteParams } from 'vue-router'
import DeckView from '@/views/DeckView.vue'
import LoginView from '@/views/LoginView.vue'
// Lazy-load hors du chemin de premier rendu ; loadAiView est exporté pour prefetch le chunk à la demande.
const ViewerComponent = () => import('@/views/Viewer/ViewerComponent.vue')
const loadAiView = () => import('@/views/AiView.vue')
const loadAboutView = () => import('@/views/AboutView.vue')
const loadDebugView = () => import('@/views/DebugView.vue')
const loadSkillsManagerView = () => import('@/views/SkillsManagerView.vue')
const loadSkillEditView = () => import('@/views/SkillEditView.vue')
const loadMapView = () => import('@/views/MapView.vue')

export { loadAiView }
import { useDeckStore } from '@/store'
import { useAiStore } from '@/store/ai'
import { preferredLocale } from '@/plugins/i18n'
import { trackEvent } from '@/lib/track'

import useHistoryState from './middlewares/useHistoryState'
import useCheckCredentials from './middlewares/useCheckCredentials'
import useDb from './middlewares/useDb'
import useAiRoute from './middlewares/useAiRoute'
import useAiAccess from './middlewares/useAiAccess'
import { buildColumnFromDeckQuery } from './utils'

const docRouteParams = {
  path: 'doc/:docId',
  component: ViewerComponent,
  props: (route: RouteLocationNormalized) => {
    const { docId } = route.params as RouteParams<'document'>
    return {
      docId,
      columnId: route.meta.columnId
    }
  },
  meta: {
    authProtected: true
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      name: 'login',
      path: '/login',
      component: LoginView
    },
    {
      name: 'about',
      path: '/about',
      component: loadAboutView
    },
    {
      name: 'debug',
      path: '/debug',
      component: loadDebugView,
      meta: {
        authProtected: true
      }
    },
    {
      name: 'changelog',
      path: '/changelog',
      component: () => import('@/views/ChangelogView.vue')
    },
    {
      name: 'settings',
      path: '/settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: {
        authProtected: true
      }
    },
    {
      name: 'ai',
      path: '/chat',
      component: loadAiView,
      meta: {
        authProtected: true
      }
    },
    {
      name: 'ai-new',
      path: '/chat/new',
      component: loadAiView,
      meta: {
        authProtected: true
      },
      beforeEnter: async to => {
        const aiStore = useAiStore()
        const id = await aiStore.createConversation()
        aiStore.clearCurrentMessages()

        const q = to.query
        let entryPoint = 'direct'
        if ('doc-id' in q || 'docId' in q) entryPoint = 'doc'
        else if (q['prompt-id'] === 'digest' || q['prompt-id'] === 'summary')
          entryPoint = 'column'
        else if ('promptId' in q || 'skill' in q) entryPoint = 'skill'
        trackEvent('ai_conversation_started', { entry_point: entryPoint })

        return {
          name: 'ai-conversation',
          params: { conversationId: id },
          query: to.query,
          replace: true
        }
      }
    },
    {
      name: 'ai-skills',
      path: '/chat/skills',
      component: loadSkillsManagerView,
      meta: {
        authProtected: true
      }
    },
    {
      name: 'ai-skill-new',
      path: '/chat/skills/new',
      component: loadSkillEditView,
      meta: {
        authProtected: true
      }
    },
    {
      name: 'ai-skill-edit',
      path: '/chat/skills/:id/edit',
      component: loadSkillEditView,
      meta: {
        authProtected: true
      }
    },
    {
      // Lance un skill via le flux ai-new (prompt-id) ; les routes statiques new et :id/edit priment.
      name: 'ai-skill-launch',
      path: '/chat/skills/:id',
      redirect: to => ({
        name: 'ai-new' as const,
        query: {
          'prompt-id': 'id' in to.params ? to.params.id : '',
          ...to.query
        }
      })
    },
    {
      name: 'ai-conversation',
      path: '/chat/:conversationId',
      component: loadAiView,
      meta: {
        authProtected: true
      },
      props: (route: RouteLocationNormalized) => ({
        promptId: route.meta.promptId,
        searchParams: route.meta.searchParams,
        docId: route.meta.docId
      }),
      children: [
        {
          name: 'ai-document',
          ...docRouteParams
        }
      ]
    },
    {
      name: 'deck',
      path: '/',
      component: DeckView,
      meta: {
        authProtected: true
      },
      children: [
        {
          name: 'document',
          ...docRouteParams
        }
      ]
    },
    {
      name: 'map',
      path: '/map',
      component: loadMapView,
      meta: {
        authProtected: true
      },
      children: [
        {
          name: 'map-document',
          ...docRouteParams
        }
      ]
    },
    {
      name: 'deck-new',
      path: '/new',
      component: DeckView,
      meta: {
        authProtected: true
      },
      beforeEnter: async to => {
        const { addColumn } = useDeckStore()
        await addColumn(
          buildColumnFromDeckQuery(to.query, preferredLocale.value)
        )
        return { name: 'deck', replace: true }
      }
    }
  ]
})

useDb(router)
useAiRoute(router)
useCheckCredentials(router)
useAiAccess(router)
useHistoryState(router)

export default router
